import dayjs from 'dayjs';
import dayjsLocale from 'dayjs/locale/pt-br';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ApmVars from './ApmVars';
import Configuracao from './Configuracao';
import ErrorUtil from './ErrorUtil';
import HotjarUtil from './HotjarUtil';
import SentryUtil from './SentryUtil';
dayjs.extend(customParseFormat);

// prettier-ignore
export const TIPO_AMBIENTE = {
    DEV:       { id: 'DEV',       descricao: 'DESENVOLVIMENTO',           urlsCorrespondentes: ['.dev.rede.tst', 'pje-dev-tst.tst', 'local', '127.', 'plenariodev'] },
    HML:       { id: 'HML',       descricao: 'HOMOLOGAÇÃO',               urlsCorrespondentes: ['.hml.rede.tst']                                                    },
    QUA:       { id: 'QUA',       descricao: 'QUALIDADE',                 urlsCorrespondentes: ['.qua.rede.tst']                                                    },
    BET:       { id: 'BET',       descricao: 'PRODUÇÃO-BETA',             urlsCorrespondentes: ['.bet.rede.tst', 'beta.tst.jus.br']                                 },
    PRD:       { id: 'PRD',       descricao: 'PRODUÇÃO',                  urlsCorrespondentes: ['.prd.rede.tst', '-api.tst.jus.br']                                 },
    DEV_2GRAU: { id: 'DEV_2GRAU', descricao: 'DESENVOLVIMENTO (2º GRAU)', urlsCorrespondentes: ['pje-dev.tst']                                                      },
    HML_2GRAU: { id: 'HML_2GRAU', descricao: 'HOMOLOGAÇÃO (2º GRAU)',     urlsCorrespondentes: ['-hml.trt', '-hml2.trt']                                            },
    PRD_2GRAU: { id: 'PRD_2GRAU', descricao: 'PRODUÇÃO (2º GRAU)',        urlsCorrespondentes: ['pje.trt']                                                          }
};

/**
 * @classdesc Agrupa variáveis do ambiente.
 */
export default class Ambiente {
    /**
     * Descrição para ambiente não identificado.
     * @type {string}
     * @readonly
     */
    static INFORMACAO_DESCONHECIDA = 'DESCONHECIDO';

    /**
     * @type {Object}
     * @readonly
     */
    static AMBIENTE_NAO_IDENTIFICADO = { id: 'UNKNOWN', descricao: 'DESCONHECIDO' };

    /** @type {Ambiente | null} */
    static #instance = null;

    /** @type {boolean} */
    static #ambienteInicializado = false;

    /** @type {null | Object} */
    static #buildInfo = null;

    /** @type {string[]} */
    static #backends = [];

    /** @type {Object} */
    static #environmentBackend = Ambiente.INFORMACAO_DESCONHECIDA;

    /** @type {string} */
    static #id = Ambiente.INFORMACAO_DESCONHECIDA;

    /** @type {string} */
    static #descricao = Ambiente.INFORMACAO_DESCONHECIDA;

    /** @type {null | Object} */
    static baseUrl = null;

    /** @type {null | Object} */
    static urls = null;

    /** @type {boolean} */
    static #sentryInicializado = false;

    /** @type {boolean} */
    static #hotjarInicializado = false;

    /** @type {Object[]} */
    static #requisicoes = [];

    constructor() {
        if (Ambiente.#instance) {
            throw new Error('Deve existir apenas uma instância.');
        }

        Ambiente.#instance = this;
    }

    static adicionarRequisicao = (url, cancelFunction) => {
        Ambiente.#requisicoes.push({ url: url, cancelar: cancelFunction });
    };

    static removerRequisicao = (url) => {
        const index = Ambiente.#requisicoes.findIndex((requisicao) => requisicao.url === url);
        index !== -1 && Ambiente.#requisicoes.splice(index, 1);
    };

    static cancelarRequisicoes = () => {
        Ambiente.#requisicoes.forEach((requisicao) => requisicao?.cancelar());
        Ambiente.#requisicoes = [];
    };

    static zerarRequisicoes = () => {
        Ambiente.#requisicoes = [];
    };

    static async consultarParametrosBuild() {
        const responseParams = await fetch(Configuracao.APP_JSON);
        const parametros = await responseParams.json();
        const formatoDataHora = 'dddd, DD/MM/YYYY HH:mm';
        dayjs.locale(dayjs.locale(dayjsLocale));
        parametros.buildAplicacao = `Build ${parametros.numeroBuild} em ${parametros.dataBuild ? dayjs(parametros.dataBuild).format(formatoDataHora) : null}`;
        return parametros;
    }

    static async inicializar() {
        if (Ambiente.#ambienteInicializado) {
            console.info('Ambiente já inicializado.');
            return true;
        }

        try {
            await Ambiente.init();
            Ambiente.#sentryInicializado = SentryUtil.inicializar();
            Ambiente.#hotjarInicializado = HotjarUtil.inicializar();
            Ambiente.#ambienteInicializado = true;
            return true;
        } catch (error) {
            throw new Error('Falha na inicialização do ambiente.', { cause: error });
        }
    }

    static isAmbienteInicializado() {
        return Ambiente.#ambienteInicializado;
    }

    static isSentryInicializado() {
        return Ambiente.#sentryInicializado;
    }

    static isHotjarInicializado() {
        return Ambiente.#hotjarInicializado;
    }

    static getId() {
        return Ambiente.#id;
    }

    static getDescricao() {
        return Ambiente.#descricao;
    }

    static getBuildInfo() {
        return Ambiente.#buildInfo;
    }

    static getBackends() {
        return Ambiente.#backends;
    }

    static getEnvBackend() {
        return Ambiente.#environmentBackend;
    }

    static getApmVars() {
        return ApmVars.getInstance();
    }

    static async init() {
        let baseUrl;

        try {
            const responseEnv = await fetch(Configuracao.CONFIG_URL);
            baseUrl = await responseEnv.json();

            Ambiente.#backends = [];
            Ambiente.#backends.push(baseUrl.sessao);
            Ambiente.#backends.push(baseUrl.acompanhamentoSessao);

            const responseConfigSessao = await fetch(`${baseUrl.sessao}/publico/configs`);
            const configSessao = await responseConfigSessao.json();
            baseUrl.logus = configSessao.urlLoginServer;
            baseUrl.envelopeHost = configSessao.urlEnvelope;
            Ambiente.baseUrl = baseUrl;
            Ambiente.#environmentBackend = baseUrl.sessao;

            const ambiente = Ambiente.identificarTipoAmbiente();
            Ambiente.#id = ambiente.id;
            Ambiente.#descricao = ambiente.descricao;

            const parametros = await Ambiente.consultarParametrosBuild();
            parametros.dataVersao = configSessao.dataVersao;
            parametros.numeroVersao = configSessao.versao;
            Ambiente.#buildInfo = parametros;
        } catch (error) {
            console.error(ErrorUtil.montarMensagemErro(error));
            throw error;
        }

        Ambiente.urls = {
            urlLogus: baseUrl.logus,
            urlHealthCheckSessao: baseUrl.sessao + '/publico/health-check',
            urlVersaoOrigem: baseUrl.sessao + '/publico/versao-origem',
            urlUsuario: baseUrl.sessao + '/usuario',
            urlOrgaosJudicantes: baseUrl.sessao + '/orgaos-judicantes/',
            urlCalendario: baseUrl.sessao + '/calendario/',
            urlOrgaosJudicantesAS: baseUrl.acompanhamentoSessao + '/orgaos-judicantes/',
            urlSessoes: baseUrl.sessao + '/sessoes/',
            urlPregoes: baseUrl.sessao + '/pregoes/',
            urlPregoesAS: baseUrl.acompanhamentoSessao + '/pregoes/',
            urlNotificar: baseUrl.envelopeHost + 'notificacao/sessao/',
            urlReceberNotificacao: baseUrl.envelopeHost + 'cometd',
            urlDocumentos: baseUrl.sessao + '/documentos/',
            urlTiposResultadosJulgamentos: baseUrl.sessao + '/resultados-julgamento/',
            urlMovimentosProcessuaisJulgamento: baseUrl.sessao + '/movimentos-processuais/sessao',
            urlValoresComplementosMovimento: baseUrl.sessao + '/movimentos-processuais/complementos',
            urlDecisoesProcessuais: baseUrl.sessao + '/decisoes-processuais/processos/sessoes',
            urlMagistrados: baseUrl.sessao + '/magistrados/',
            urlFotosMagistrados: baseUrl.sessao + '/foto/',
            urlDescricoesCargos: baseUrl.sessao + '/magistrados/descricoes-cargo-genero/',
            urlParametros: baseUrl.sessao + '/parametros',
            urlParametrosPublico: baseUrl.sessao + '/publico/parametros',
            urlFeaturesFlags: baseUrl.sessao + '/publico/parametros/feature-flags',
            urlClasses: baseUrl.sessao + '/classes',
            urlProcuradores: baseUrl.sessao + '/procuradores',
            urlRepresentantes: baseUrl.sessao + '/representantes',
            urlSignatariosCertidao: baseUrl.sessao + '/signatarios-certidao',
            urlVotosInvalidos: baseUrl.sessao + '/votos-invalidos/sessoes/',
            urlPautas: baseUrl.sessao + '/pautas',
            urlProcessos: baseUrl.sessao + '/processos',
            urlBaseSessao: baseUrl.sessao,
            urlSessaoInscricaoAdvogado: baseUrl.sessao + '/inscricoes-advogado/'
        };
    }

    static identificarTipoAmbiente() {
        for (const tipoAmbiente of Object.values(TIPO_AMBIENTE)) {
            for (const url of tipoAmbiente.urlsCorrespondentes) {
                if (Ambiente.#environmentBackend.indexOf(url) !== -1) {
                    return tipoAmbiente;
                }
            }
        }

        console.warn('Tipo de ambiente não identificado.');
        return Ambiente.AMBIENTE_NAO_IDENTIFICADO.descricao;
    }

    static async esperarInicializacaoAmbiente() {
        const INTERVALO_VERIFICACAO = 200;

        const p = await new Promise((resolve) => {
            const verificadorInicializacaoAmbienteIntervalID = setInterval(testarInicializacao, INTERVALO_VERIFICACAO);

            function testarInicializacao() {
                console.info(
                    `Aguardando inicialização do ambiente... [ID=${verificadorInicializacaoAmbienteIntervalID}]`
                );

                if (Ambiente.isAmbienteInicializado()) {
                    console.info(`Ambiente inicializado. [ID=${verificadorInicializacaoAmbienteIntervalID}]`);
                    clearInterval(verificadorInicializacaoAmbienteIntervalID);
                    resolve(true);
                }
            }
        });

        return p;
    }
}
